/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './ProjectWiseExpenseReport.scss';

const PROJECT_WISE_EXPENSE_TABLE_HEIGHT = 'calc(100vh - 250px)';
import { TableUtility } from '@utility/dynamic-table';
import MilestoneInitial from '../MilestoneMIS/MilestoneInitial';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { Toast } from 'primereact/toast';

import { useAuth } from '../../../Hooks/useAuth';
import {
  appendServiceNameToURL,
  appendServicePortToURL,
} from '../../../Api/resources/serviceURLs';
import { serviceName } from '../../../Api/resources/serviceConstants';
import { PROJECT_WISE_EXPENSE_REPORT } from '../../../Api/resources/endPoint';
import { getExportFileName, getExportFileNameWithDate } from '../../Delivery/utils';

const fetchBaseURL = () => {
  const targetService = serviceName.PROJECT_MS || serviceName.RESELLER_SERVICE;
  const serviceURL = appendServiceNameToURL(targetService);
  const servicePort = appendServicePortToURL(targetService);
  let baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  if (!baseUrl) {
    if (
      process.env.NODE_ENV === 'production'
      && typeof window !== 'undefined'
      && window.location
      && window.location.origin
    ) {
      baseUrl = window.location.origin.replace(/\/$/, '');
    } else {
      baseUrl = 'http://localhost';
    }
  }

  const endpoint = PROJECT_WISE_EXPENSE_REPORT || '/v1/project-wise-expense-report';

  if (process.env.NODE_ENV === 'production') {
    return `${baseUrl}${serviceURL}/api${endpoint}`;
  }

  const hasPort = /:\d+$/.test(baseUrl);
  if (hasPort) {
    return `${baseUrl}${serviceURL}/api${endpoint}`;
  }

  return `${baseUrl}:${servicePort}${serviceURL}/api${endpoint}`;
};

const baseURL = fetchBaseURL();

const isMonthFilter = (item) => {
  const template = (item?.template || '').toString().toLowerCase();
  const type = (item?.type || '').toString().toLowerCase();
  const pickerType = (item?.pickerType || '').toString().toLowerCase();
  const displayFormat = (item?.displayFormat || '').toString().toLowerCase();

  return (
    template === 'monthcalendar'
    || template === 'month'
    || type === 'month'
    || pickerType === 'month'
    || displayFormat === 'yyyy-mm'
  );
};

const toMonthCalendarValue = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return `${value.getMonth() + 1}/${value.getFullYear()}`;
  }

  const normalized = value.toString().trim();
  if (!normalized) return null;

  const yearMonthMatch = normalized.match(/^(\d{4})-(\d{1,2})$/);
  if (yearMonthMatch) {
    const [, year, month] = yearMonthMatch;
    return `${Number(month)}/${year}`;
  }

  const monthYearMatch = normalized.match(/^(\d{1,2})[/-](\d{4})$/);
  if (monthYearMatch) {
    const [, month, year] = monthYearMatch;
    return `${Number(month)}/${year}`;
  }

  return normalized;
};

const normalizeMonthYearPart = (yearPart) => {
  const normalizedYear = String(yearPart || '').trim();
  if (!normalizedYear) return null;
  if (normalizedYear.length === 2) return `20${normalizedYear}`;
  return /^\d{4}$/.test(normalizedYear) ? normalizedYear : null;
};

const toApiMonthRangeValue = (value, { useEndOfMonth = false } = {}) => {
  if (!value) return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const year = value.getFullYear();
    const month = value.getMonth() + 1;
    const day = useEndOfMonth ? new Date(year, month, 0).getDate() : 1;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  const normalized = String(value).trim();
  if (!normalized) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return normalized;

  const yearMonthMatch = normalized.match(/^(\d{4})-(\d{1,2})$/);
  if (yearMonthMatch) {
    const [, year, month] = yearMonthMatch;
    const lastDay = new Date(Number(year), Number(month), 0).getDate();
    return `${year}-${String(Number(month)).padStart(2, '0')}-${String(useEndOfMonth ? lastDay : 1).padStart(2, '0')}`;
  }

  const numericMonthYearMatch = normalized.match(/^(\d{1,2})[/-](\d{2}|\d{4})$/);
  if (numericMonthYearMatch) {
    const [, monthPart, yearPart] = numericMonthYearMatch;
    const year = normalizeMonthYearPart(yearPart);
    const month = Number(monthPart);
    if (!year || Number.isNaN(month) || month < 1 || month > 12) return null;
    const lastDay = new Date(Number(year), month, 0).getDate();
    return `${year}-${String(month).padStart(2, '0')}-${String(useEndOfMonth ? lastDay : 1).padStart(2, '0')}`;
  }

  const monthNameYearMatch = normalized.match(/^([A-Za-z]{3,9})[-/\s](\d{2}|\d{4})$/);
  if (monthNameYearMatch) {
    const [, monthName, yearPart] = monthNameYearMatch;
    const year = normalizeMonthYearPart(yearPart);
    if (!year) return null;
    const parsed = new Date(`${monthName} 1, ${year}`);
    if (Number.isNaN(parsed.getTime())) return null;
    return toApiMonthRangeValue(parsed, { useEndOfMonth });
  }

  const parsedDate = new Date(normalized);
  if (Number.isNaN(parsedDate.getTime())) return null;
  return toApiMonthRangeValue(parsedDate, { useEndOfMonth });
};

const extractMonthRangePayload = (container) => {
  const inputs = Array.from(container?.querySelectorAll('input') || []);
  const [fromInput, toInput] = inputs;

  return {
    txtSDate: toApiMonthRangeValue(fromInput?.value || null),
    txtSDate_to: toApiMonthRangeValue(toInput?.value || null, { useEndOfMonth: true }),
  };
};

const normalizeMenuItems = (menuItems = []) => (
  Array.isArray(menuItems)
    ? menuItems.map((menuItem) => ({
      ...menuItem,
      items: Array.isArray(menuItem?.items)
        ? menuItem.items.map((item) => {
          if (item?.type !== 'filter') return item;
          const normalizedTemplate = (item?.template || '').toString().toLowerCase();
          if (normalizedTemplate === 'daterange') return item;
          if (!isMonthFilter(item)) return item;

          return {
            ...item,
            template: 'monthCalendar',
            selectedValue: toMonthCalendarValue(item?.selectedValue ?? item?.value ?? null),
          };
        })
        : [],
    }))
    : []
);

const MONTH_HEADER_PATTERN = /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\s*\d{2,4}$/i;

const isNumericColumnHeader = (hdr) => {
  if (!hdr) return false;
  const text = (hdr.textContent || '').toLowerCase().trim();
  if (text === 'total' || text === 'rowtotal' || MONTH_HEADER_PATTERN.test(text)) return true;
  const field = hdr.getAttribute('data-field') || hdr.getAttribute('field') || '';
  if (field.startsWith('exp_') || field === 'rowTotal') return true;
  return false;
};

const isManagerColumnHeader = (hdr) => {
  if (!hdr) return false;
  const text = (hdr.textContent || '').toLowerCase();
  return text.includes('manager');
};

// The generic column renderer used by the table-utility package ignores the
// `body`/`width` fields from the column config, so the ellipsis + hover title
// for this column has to be applied directly on the rendered DOM cells.
const applyManagerColumnEllipsis = (table, colIndex) => {
  const bodyCells = table.querySelectorAll(
    `.p-datatable-tbody tr td:nth-child(${colIndex}), tbody tr td:nth-child(${colIndex})`,
  );

  bodyCells.forEach((cell) => {
    cell.style.maxWidth = '220px';
    cell.style.overflow = 'hidden';
    cell.style.textOverflow = 'ellipsis';
    cell.style.whiteSpace = 'nowrap';

    const content = cell.querySelector('div') || cell;
    const fullText = (content.textContent || '').trim();
    content.style.overflow = 'hidden';
    content.style.textOverflow = 'ellipsis';
    content.style.whiteSpace = 'nowrap';

    if (fullText) {
      cell.setAttribute('title', fullText);
      content.setAttribute('title', fullText);
    }
  });
};

const NUMERIC_CELL_FORMATTER = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const applyNumericAlignment = () => {
  const container = document.querySelector('.project-wise-expense-report-table-utility')
    || document.querySelector('.project-wise-expense-report');
  if (!container) return;

  const tbodies = container.querySelectorAll('.p-datatable-tbody, tbody');
  tbodies.forEach((tbody) => {
    const rows = Array.from(tbody.querySelectorAll('tr'));
    let totalRow = null;
    rows.forEach((row) => {
      const firstCellText = row.cells[0]?.innerText?.trim() || row.cells[0]?.textContent?.trim() || '';
      if (firstCellText === 'Total' || row.classList.contains('project-wise-expense-report-total-row')) {
        row.classList.add('project-wise-expense-report-total-row');
        totalRow = row;
      }
    });

    if (totalRow && tbody.lastElementChild !== totalRow) {
      tbody.appendChild(totalRow);
    }
  });

  const tables = container.querySelectorAll('table, .p-datatable-table');
  if (!tables.length) return;

  tables.forEach((table) => {
    const headerCells = table.querySelectorAll('.p-datatable-thead tr th, thead tr th');
    headerCells.forEach((hdr) => {
      const isNumeric = isNumericColumnHeader(hdr);

      const colIndex = Array.from(hdr.parentElement.children).indexOf(hdr) + 1;
      const bodyCells = table.querySelectorAll(
        `.p-datatable-tbody tr td:nth-child(${colIndex}), tbody tr td:nth-child(${colIndex})`,
      );

      if (isManagerColumnHeader(hdr)) {
        applyManagerColumnEllipsis(table, colIndex);
      }

      if (isNumeric) {
        hdr.classList.add('numeric-aligned');
        hdr.style.textAlign = 'right';

        const headerContent = hdr.querySelector('.p-column-header-content');
        if (headerContent) {
          headerContent.style.justifyContent = 'flex-end';
          headerContent.style.textAlign = 'right';
        }

        const headerCol = hdr.querySelector('.header-column');
        if (headerCol) {
          headerCol.style.justifyContent = 'flex-end';
          headerCol.style.textAlign = 'right';
          const span = headerCol.querySelector('span');
          if (span) {
            span.style.textAlign = 'right';
            span.style.width = '100%';
          }
        }

        bodyCells.forEach((cell) => {
          cell.classList.add('numeric-aligned');
          cell.style.textAlign = 'right';
          const div = cell.querySelector('div');
          if (div) {
            div.style.textAlign = 'right';
            div.style.justifyContent = 'flex-end';
          }
        });
      } else {
        hdr.classList.remove('numeric-aligned');
        hdr.style.textAlign = 'left';

        const headerContent = hdr.querySelector('.p-column-header-content');
        if (headerContent) {
          headerContent.style.justifyContent = 'flex-start';
          headerContent.style.textAlign = 'left';
        }

        const headerCol = hdr.querySelector('.header-column');
        if (headerCol) {
          headerCol.style.justifyContent = 'flex-start';
          headerCol.style.textAlign = 'left';
          const span = headerCol.querySelector('span');
          if (span) {
            span.style.textAlign = 'left';
          }
        }

        bodyCells.forEach((cell) => {
          cell.classList.remove('numeric-aligned');
          cell.style.textAlign = 'left';
          const div = cell.querySelector('div');
          if (div) {
            div.style.textAlign = 'left';
            div.style.justifyContent = 'flex-start';
          }
        });
      }
    });
  });
};

const formatNumericCellValue = (value) => {
  if (value === undefined || value === null || value === '') return value;
  const numericValue = typeof value === 'number'
    ? value
    : Number(
      String(value)
        .replace(/,/g, '')
        .trim(),
    );
  if (!Number.isFinite(numericValue)) return value;
  return NUMERIC_CELL_FORMATTER.format(numericValue);
};

const applyExpenseFormatting = (tableConfiguration, tableData) => {
  const safeConfig = Array.isArray(tableConfiguration) ? tableConfiguration : [];
  const numericFields = safeConfig
    .filter((col) => {
      const headerText = (col?.header || '').toLowerCase();
      const fieldName = (col?.field || '').toLowerCase();
      return Boolean(col?.numeric)
        || col?.align === 'right'
        || fieldName.startsWith('exp_')
        || fieldName === 'rowtotal'
        || MONTH_HEADER_PATTERN.test(headerText);
    })
    .map((col) => col.field);

  if (!numericFields.length) return Array.isArray(tableData) ? tableData : [];

  const formatRow = (row) => {
    if (!row || typeof row !== 'object') return row;
    const updatedRow = { ...row };
    numericFields.forEach((field) => {
      if (Object.prototype.hasOwnProperty.call(updatedRow, field)) {
        updatedRow[field] = formatNumericCellValue(updatedRow[field]);
      }
    });
    if (Array.isArray(row.children)) {
      updatedRow.children = row.children.map(formatRow);
    }
    return updatedRow;
  };

  return tableData.map(formatRow);
};

const handleCellClick = (arg1, arg2) => {
  const fieldPath = arg1?.fieldPath || arg2?.field || arg2?.columnKey || arg1?.field;
  const rowData = arg1?.rowData || arg1;
  const isProjectNameCol = fieldPath === 'projectName' || fieldPath === 'project_name';
  const idValue = rowData?.projectId || rowData?.project_seq_id || rowData?.project_id;
  if (isProjectNameCol && idValue) {
    const targetUrl = `/delivery/project-details?projectId=${encodeURIComponent(String(idValue))}`;
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  }
};

const applyProjectNameLink = (data) => {
  if (!Array.isArray(data)) return data;

  const formatRow = (row) => {
    if (!row || typeof row !== 'object') return row;

    const isTotalRow = row.projectName === 'Total' || row.isTotalRow === true || row._isFooterRow === true;
    if (isTotalRow) {
      const existingConfig = row.rowConfiguration || {};
      const existingOnClick = Array.isArray(existingConfig.onClick) ? existingConfig.onClick : [];
      const filteredOnClick = existingOnClick.filter(
        (item) => item?.field !== 'projectName' && item?.field !== 'project_name',
      );
      return {
        ...row,
        rowConfiguration: {
          ...existingConfig,
          onClick: filteredOnClick,
        },
      };
    }

    const existingConfig = row.rowConfiguration || {};
    const existingOnClick = Array.isArray(existingConfig.onClick) ? existingConfig.onClick : [];
    const fieldsToAdd = ['projectName', 'project_name'].filter(
      (field) => !existingOnClick.some((item) => item?.field === field),
    );

    const updatedRow = {
      ...row,
      ...(fieldsToAdd.length > 0
        ? {
          rowConfiguration: {
            ...existingConfig,
            onClick: [
              ...existingOnClick,
              ...fieldsToAdd.map((field) => ({ field })),
            ],
          },
        }
        : {}),
    };

    if (Array.isArray(row.children)) {
      updatedRow.children = row.children.map(formatRow);
    }
    return updatedRow;
  };

  return data.map(formatRow);
};


const createRightAlignedCellRenderer = (field) => {
  const CellRenderer = (rowData) => {
    const rawVal = rowData?.[field];
    let val = '-';
    if (rawVal != null) {
      const num = Number(rawVal);
      val = !Number.isNaN(num)
        ? num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : String(rawVal);
    }
    return (
      <div
        title={val}
        style={{
          textAlign: 'right',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%',
          display: 'block',
          boxSizing: 'border-box',
        }}
      >
        {val}
      </div>
    );
  };
  CellRenderer.displayName = `RightAlignedCell_${field}`;
  return CellRenderer;
};

const handleFilteredApiResponse = (response, tableConfigurationCacheRef, tableConfigInjectedRef) => {
  if (!response) return;

  let rawTableConfiguration = [];
  if (Array.isArray(response.data?.tableConfiguration)) {
    rawTableConfiguration = response.data.tableConfiguration;
  } else if (Array.isArray(response.data?.data?.tableConfiguration)) {
    rawTableConfiguration = response.data.data.tableConfiguration;
  } else if (Array.isArray(response.tableConfiguration)) {
    rawTableConfiguration = response.tableConfiguration;
  } else if (Array.isArray(response.data)) {
    rawTableConfiguration = response.data;
  }

  const safeRawConfig = Array.isArray(rawTableConfiguration) ? rawTableConfiguration : [];
  const normalizedTableConfiguration = safeRawConfig.map((column) => {
    const field = column?.field || '';
    const isDynamicMonthCol = field.startsWith('exp_');
    const isRowTotal = field === 'rowTotal';
    const isAmountCol = isDynamicMonthCol || isRowTotal || Boolean(column?.numeric);
    const isManagerCol = field === 'managers';

    if (isAmountCol) {
      return {
        ...column,
        align: 'right',
        headerAlign: 'right',
        bodyAlign: 'right',
        numeric: true,
        className: 'numeric-aligned',
        headerClassName: 'numeric-aligned',
        cellClassName: 'numeric-aligned',
        body: createRightAlignedCellRenderer(field),
        menuItems: normalizeMenuItems(Array.isArray(column?.menuItems) ? column.menuItems : []),
      };
    }

    return {
      ...column,
      align: 'left',
      headerAlign: 'left',
      bodyAlign: 'left',
      numeric: false,
      menuItems: normalizeMenuItems(Array.isArray(column?.menuItems) ? column.menuItems : []),
      ...(isManagerCol
        ? {
          width: column?.width || 220,
          // Custom `body` renderers are ignored by the generic column renderer used here;
          // `ellipsis: true` is the flag it honors to truncate + show a hover tooltip.
          ellipsis: true,
        }
        : {}),
    };
  });

  if (normalizedTableConfiguration.length > 0) {
    const projectNameCol = normalizedTableConfiguration.find(
      (col) => col?.field === 'projectName',
    );
    const projectIdCol = normalizedTableConfiguration.find(
      (col) => col?.field === 'projectId',
    );
    const remainingCols = normalizedTableConfiguration.filter(
      (col) => col?.field !== 'projectName' && col?.field !== 'projectId',
    );

    rawTableConfiguration = [
      ...(projectNameCol
        ? [{ ...projectNameCol, pinned: true, defaultPinned: true, width: 200, headerAlign: 'left' }]
        : []),
      ...(projectIdCol ? [{ ...projectIdCol, pinned: true, defaultPinned: true, width: 100 }] : []),
      ...remainingCols,
    ];
    if (tableConfigurationCacheRef) tableConfigurationCacheRef.current = rawTableConfiguration;
  } else if (Array.isArray(tableConfigurationCacheRef?.current) && tableConfigurationCacheRef.current.length) {
    rawTableConfiguration = tableConfigurationCacheRef.current;
  } else {
    rawTableConfiguration = Array.isArray(normalizedTableConfiguration) ? normalizedTableConfiguration : [];
  }

  const shouldInjectConfig = tableConfigInjectedRef
    ? !tableConfigInjectedRef.current && rawTableConfiguration.length > 0
    : true;

  if (shouldInjectConfig && tableConfigInjectedRef) {
    tableConfigInjectedRef.current = true;
  }

  const rawTableData = response.data?.tableData
    || response.data?.data?.tableData
    || response.data?.data
    || [];
  const validTableData = Array.isArray(rawTableData) ? rawTableData : [];

  const tableDataWithTotal = [...validTableData];
  if (validTableData.length > 0) {
    const hasTotalRow = validTableData.some(
      (row) => row?.projectName === 'Total' || row?.isTotalRow === true || row?._isFooterRow === true,
    );

    if (!hasTotalRow) {
      const totalsObj = response.data?.totals || response.data?.data?.totals;
      const totalRowFromApi = response.data?.totalRow || response.data?.data?.totalRow;

      if (totalRowFromApi) {
        tableDataWithTotal.push(totalRowFromApi);
      } else if (totalsObj) {
        const monthTotals = totalsObj.monthTotals || {};
        tableDataWithTotal.push({
          projectName: 'Total',
          projectId: '',
          clientName: '',
          projectVertical: '',
          projectType: '',
          projectStatus: '',
          managers: '',
          baselineStartDate: '',
          baselineEndDate: '',
          actualStartDate: '',
          replannedEndDate: '',
          rowTotal: totalsObj.overallTotal ?? 0,
          ...monthTotals,
          rowConfiguration: {
            isFrozen: true,
            template: [
              { field: 'projectName', fontWeight: '700' },
              { field: 'rowTotal', fontWeight: '700' },
            ],
          },
          _isFooterRow: true,
          isTotalRow: true,
        });
      }
    }
  }

  const safeRawTableConfig = Array.isArray(rawTableConfiguration) ? rawTableConfiguration : [];
  const formattedTableData = applyExpenseFormatting(
    safeRawTableConfig,
    tableDataWithTotal,
  );
  const dataWithProjectLink = applyProjectNameLink(formattedTableData);

  response.data = {
    ...response.data,
    tableConfiguration: rawTableConfiguration,
    tableData: dataWithProjectLink,
    data: {
      ...(response.data?.data || {}),
      tableConfiguration: rawTableConfiguration,
      tableData: dataWithProjectLink,
    },
  };

  const backendMeta = response.data?.meta || response.data?.data?.meta || {};
  const hasTotal = dataWithProjectLink.some(
    (row) => row?.projectName === 'Total' || row?.isTotalRow === true || row?._isFooterRow === true,
  );
  const dataCount = hasTotal ? Math.max(0, dataWithProjectLink.length - 1) : dataWithProjectLink.length;
  const totalRecords = Number(backendMeta.total ?? response.meta?.total ?? dataCount ?? 0);

  response.meta = {
    ...(response.meta || {}),
    total: totalRecords,
    page: Number(backendMeta.page ?? response.meta?.page ?? 1),
    limit: Number(backendMeta.limit ?? response.meta?.limit ?? 10),
  };
};

const ToolBarHeading = () => (
  <div
    className={[
      'project-employee-cost-toolbar-heading',
      'project-wise-expense-report-toolbar-heading',
    ].join(' ')}
  >
    <span
      className={[
        'project-employee-cost-toolbar-heading-title',
        'project-wise-expense-report-toolbar-heading-title',
      ].join(' ')}
    >
      Project Wise Expense Report
    </span>
    <div
      className={[
        'project-employee-cost-toolbar-heading-right',
        'project-wise-expense-report-toolbar-heading-right',
      ].join(' ')}
    >
      <span
        className={[
          'project-employee-cost-toolbar-heading-note',
          'project-wise-expense-report-toolbar-heading-note',
        ].join(' ')}
      >
        <strong>Note:</strong> The amounts in the table are in INR format
      </span>
      <span
        className={[
          'project-employee-cost-toolbar-heading-separator',
          'project-wise-expense-report-toolbar-heading-separator',
        ].join(' ')}
        aria-hidden="true"
      />
    </div>
  </div>
);

const useProjectWiseExpenseReportEffects = ({
  containerRef,
  resetToCurrentDate,
  setSelectedMonthRange,
  getSelectedMonthRange,
  setTableScrollHeight,
  onApplyFilters,
  toastRef,
}) => {
  useEffect(() => {
    document.body.classList.add('project-employee-cost-page');
    document.body.classList.add('project-wise-expense-report-page');
    return () => {
      document.body.classList.remove('project-employee-cost-page');
      document.body.classList.remove('project-wise-expense-report-page');
    };
  }, []);

  useEffect(() => {
    const observer = new MutationObserver(applyNumericAlignment);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const rootNode = containerRef.current;

    const handleResetClick = (event) => {
      if (!rootNode) return;
      const resetButton = event.target.closest('button');
      if (!resetButton || !rootNode.contains(resetButton)) return;
      const clickedResetIcon = Boolean(event.target.closest('.pi-refresh'))
        || Boolean(resetButton.querySelector('.pi-refresh'));
      if (clickedResetIcon) resetToCurrentDate();
    };

    rootNode?.addEventListener('click', handleResetClick);

    const handleResize = () => {
      setTableScrollHeight(PROJECT_WISE_EXPENSE_TABLE_HEIGHT);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      rootNode?.removeEventListener('click', handleResetClick);
      window.removeEventListener('resize', handleResize);
    };
  }, [containerRef, resetToCurrentDate, setTableScrollHeight]);

  useEffect(() => {
    const clearSelectedMonthRange = () => {
      setSelectedMonthRange({ txtSDate: null, txtSDate_to: null });
    };

    const syncSelectedMonthRange = (container) => {
      const nextRange = extractMonthRangePayload(container);
      setSelectedMonthRange(nextRange);
      return nextRange;
    };

    const handleFilterFooterClick = (event) => {
      const mainApplyBtn = event.target.closest('.tu-filter-footer-apply');
      if (mainApplyBtn) {
        const calendarContainer = document.querySelector('.month-wise-calendar-filter-container');
        // The calendar container may already be closed if the month was applied
        // earlier via its own Apply button, so fall back to the persisted range.
        const currentRange = calendarContainer
          ? syncSelectedMonthRange(calendarContainer)
          : getSelectedMonthRange?.();

        // Missing month: let the table utility's own mandatory-field validation
        // popup handle it, so skip triggering the apply/API call here.
        if (!currentRange?.txtSDate || !currentRange?.txtSDate_to) {
          return;
        }

        if (onApplyFilters) onApplyFilters(true);
        return;
      }

      const footerReset = event.target.closest('.tu-filter-footer-reset');
      if (footerReset) {
        clearSelectedMonthRange();
        if (onApplyFilters) onApplyFilters(false);
        return;
      }

      const targetBtn = event.target.closest('button') || event.target.closest('.p-button');
      if (targetBtn) {
        const labelEl = targetBtn.querySelector('.p-button-label');
        const label = (labelEl ? labelEl.textContent : targetBtn.textContent || '').trim();
        if (label === 'Reset All' || label === 'Clear') {
          clearSelectedMonthRange();
          if (onApplyFilters) onApplyFilters(false);
          return;
        }
      }

      const calendarButton = event.target.closest('.month-wise-calendar-filter-container .p-button');
      if (!calendarButton) return;

      const monthContainer = calendarButton.closest('.month-wise-calendar-filter-container');
      const labelEl = calendarButton.querySelector('.p-button-label');
      const buttonLabel = labelEl?.textContent?.trim();

      if (buttonLabel === 'Reset') {
        clearSelectedMonthRange();
        if (onApplyFilters) onApplyFilters(false);
        return;
      }

      if (buttonLabel !== 'Apply') return;

      syncSelectedMonthRange(monthContainer);
    };

    document.addEventListener('click', handleFilterFooterClick, true);
    return () => document.removeEventListener('click', handleFilterFooterClick, true);
  }, [setSelectedMonthRange, getSelectedMonthRange, onApplyFilters, toastRef]);
};

const ProjectWiseExpenseReport = () => {
  const { userProfile } = useAuth();
  const containerRef = useRef(null);
  const selectedMonthRangeRef = useRef({ txtSDate: null, txtSDate_to: null });
  const userAppliedFiltersRef = useRef(false);

  const [tableScrollHeight, setTableScrollHeight] = useState(PROJECT_WISE_EXPENSE_TABLE_HEIGHT);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const [, setLoading] = useState(false);
  const toastRef = useRef(null);
  const tableConfigurationCacheRef = useRef([]);
  const tableConfigInjected = useRef(false);
  const tableStateHandlerRef = useRef(null);

  const setSelectedMonthRange = useCallback((nextRange) => {
    selectedMonthRangeRef.current = {
      txtSDate: nextRange?.txtSDate || null,
      txtSDate_to: nextRange?.txtSDate_to || null,
    };
  }, []);

  const handleFilteredApiResponseWithState = useCallback((response) => {
    setLoading(false);
    const hasFilterError = response?.data?.success === false
      || response?.data?.message === 'Please select the month';

    if (userAppliedFiltersRef.current && hasFilterError) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'Validation',
        detail: response?.data?.message || 'Please select the month',
        life: 3000,
      });
    }
    handleFilteredApiResponse(response, tableConfigurationCacheRef, tableConfigInjected);
  }, []);

  const resetToCurrentDate = useCallback(() => {
    userAppliedFiltersRef.current = false;
    tableConfigInjected.current = false;
    setSelectedMonthRange({ txtSDate: null, txtSDate_to: null });
    setHasAppliedFilters(false);
    setLoading(false);
  }, []);

  const handleApplyFiltersState = useCallback((applied) => {
    const isApplied = Boolean(applied);
    userAppliedFiltersRef.current = isApplied;
    setHasAppliedFilters(isApplied);
    if (isApplied) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, []);

  useProjectWiseExpenseReportEffects({
    containerRef,
    resetToCurrentDate,
    setSelectedMonthRange,
    getSelectedMonthRange: () => selectedMonthRangeRef.current,
    setTableScrollHeight,
    onApplyFilters: handleApplyFiltersState,
    toastRef,
  });

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: true,
      baseURL,

      userInfo: {
        emailId: userProfile.email,
        permissions: userProfile.permissions,
        roleDetails: userProfile.roleDetails,
        employeeId: userProfile.employeeId,
        globalColors: userProfile?.siteConfiguration?.global_colors,
      },

      apiEndpoints: {
        getAllViews: { endpoint: '', enabled: false },
        getFilteredData: { endpoint: '', enabled: true },
        saveOrUpdateView: { endpoint: 'view', enabled: true },
        deleteView: { endpoint: 'view-delete', enabled: false },
        download: {
          endpoint: 'export',
          enabled: true,
          filename: () => getExportFileName('Project Wise Expense Report'),
          sheetName: () => getExportFileNameWithDate('Project Wise Expense Report'),
        },
      },

      handler: {
        onCellClickHandler: handleCellClick,
        handleFilteredApiResponse: handleFilteredApiResponseWithState,
        setTableStateHandler: (getTableState) => {
          tableStateHandlerRef.current = getTableState();
        },
      },

      additionalPayloadData: {
        get txtSDate() {
          return selectedMonthRangeRef.current.txtSDate;
        },
        get txtSDate_to() {
          return selectedMonthRangeRef.current.txtSDate_to;
        },
      },

      headers: {
        token: userProfile.access_token,
      },

      showToolBar: true,
      showPagination: true,
      showTieredMenu: true,

      toolBarSettings: {
        showAddNew: false,
        showDownloadExcel: true,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showToolBarHeading: true,
        toolBarHeading: <ToolBarHeading />,
        showSaveView: false,
        showReset: false,
        showExpandCollapse: false,
        showFilterOption: true,
      },
    },

    tableSettings: {
      size: 'small',
      loaderSkeleton: { rows: 8, bodyCells: 2, columns: 7 },
      showExpandCollapse: false,
      tableScrollHeight,
      maxFrozenColumns: { desktop: 1, tablet: 1, mobile: 1 },
      paginationConfig: { page: 1, limit: 10, rowsPerPageOptions: [10, 20, 50, 100] },
    },
  }), [tableScrollHeight, handleFilteredApiResponseWithState, userProfile]);

  const isInitialState = !hasAppliedFilters;
  const containerClassName = [
    'project-employee-cost-outer-layer',
    'project-wise-expense-report-outer-layer',
    isInitialState ? 'project-employee-cost--initial project-wise-expense-report--initial' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={containerRef}
      className={containerClassName}
    >
      <Toast ref={toastRef} />
      <div className="project-employee-cost project-wise-expense-report">
        <div className="project-employee-cost-table-utility project-wise-expense-report-table-utility">
          <TableUtility configuration={configuration} />
        </div>
        {isInitialState && <MilestoneInitial />}
      </div>
    </div>
  );
};

export default ProjectWiseExpenseReport;
